import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, PawPrint } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/store/ProductCard";
import { Reveal } from "@/components/store/Reveal";
import { SPECIES, SPECIES_CATEGORIES, catLabel, ALL_CATEGORIES } from "@/lib/taxonomy";
import { useProductModal } from "@/context/ProductModalContext";
import { formatPrice, applyDiscount } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { track } from "@/lib/track";

const SPECIES_CHIPS = [{ slug: "todos", name: "Todas" }, ...SPECIES.map((s) => ({ slug: s.slug, name: s.name }))];

export default function Tienda() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recientes");
  const [focused, setFocused] = useState(false);
  const { openProduct } = useProductModal();
  const { settings } = useSettings();

  const selectedCats = useMemo(() => {
    const raw = params.get("categoria") || "";
    return raw.split(",").map((c) => c.trim()).filter(Boolean);
  }, [params]);
  const species = params.get("especie") || "todos";

  const toggleCategory = (c) => {
    const p = new URLSearchParams(params);
    if (c === "todos") {
      p.delete("categoria");
    } else {
      const set = new Set(selectedCats);
      if (set.has(c)) { set.delete(c); } else { set.add(c); track("category_view", { category: c, pet_type: species }); }
      const next = Array.from(set);
      next.length ? p.set("categoria", next.join(",")) : p.delete("categoria");
    }
    setParams(p);
  };
  const setSpecies = (s) => {
    const p = new URLSearchParams(params);
    s === "todos" ? p.delete("especie") : p.set("especie", s);
    p.delete("categoria"); // reset categories when species changes
    setParams(p);
  };

  useEffect(() => {
    setLoading(true);
    api.get("/products").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 3) return;
    const id = setTimeout(() => track("search", { query: q }), 900);
    return () => clearTimeout(id);
  }, [search]);

  // categories available depend on selected species
  const categoryChips = useMemo(() => {
    const slugs = species !== "todos" && SPECIES_CATEGORIES[species] ? SPECIES_CATEGORIES[species] : ALL_CATEGORIES;
    return [{ slug: "todos", name: "Todas" }, ...slugs.map((s) => ({ slug: s, name: catLabel(s) }))];
  }, [species]);

  const filtered = useMemo(() => {
    let list = products;
    if (species !== "todos") list = list.filter((p) => p.pet_type === species || p.pet_type === "todos");
    if (selectedCats.length) list = list.filter((p) => {
      const cats = (p.categories && p.categories.length) ? p.categories : [p.category];
      return selectedCats.some((c) => cats.includes(c));
    });
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
    }
    list = [...list];
    if (sort === "precio-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "precio-desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, selectedCats, species, search, sort]);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return products
      .filter((p) => p.name.toLowerCase().includes(q) || catLabel(p.category).toLowerCase().includes(q))
      .slice(0, 6);
  }, [products, search]);

  return (
    <div className="max-w-6xl mx-auto px-5 pt-10 sm:pt-14">
      <Reveal>
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-coral">Catálogo</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-charcoal tracking-tight mt-3">La tienda</h1>
        <p className="text-muted-moss mt-2 max-w-xl">Filtra por especie o categoría y arma tu pedido. Coordinamos entrega y pago por WhatsApp.</p>
      </Reveal>

      {/* search + sort */}
      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-moss z-10" />
          <input data-testid="catalog-search-input" value={search} onChange={(e) => setSearch(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)} placeholder="Buscar productos..." className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white border border-[#E3E7E2] text-sm outline-none focus:border-coral" />
          {focused && suggestions.length > 0 && (
            <div data-testid="search-suggestions" className="absolute z-20 top-full mt-2 inset-x-0 bg-white rounded-2xl border border-[#E3E7E2] shadow-xl overflow-hidden animate-float-up">
              {suggestions.map((p) => {
                const disc = Number(p.discount) || 0;
                return (
                  <button
                    key={p.id}
                    data-testid={`suggestion-${p.id}`}
                    onMouseDown={(e) => { e.preventDefault(); openProduct(p); setFocused(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-cream-2 transition-colors text-left"
                  >
                    <img src={p.image_url} alt="" className="w-11 h-11 rounded-lg object-cover bg-cream-2 shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-charcoal truncate">{p.name}</span>
                      <span className="block text-xs text-muted-moss">{catLabel(p.category)}</span>
                    </span>
                    <span className="text-sm font-bold text-coral shrink-0">{formatPrice(applyDiscount(p.price, disc), settings.currency)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 bg-white border border-[#E3E7E2] rounded-2xl px-3">
          <SlidersHorizontal className="w-4 h-4 text-muted-moss" />
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent py-3 text-sm text-charcoal outline-none">
            <option value="recientes">Recientes</option>
            <option value="precio-asc">Precio: menor a mayor</option>
            <option value="precio-desc">Precio: mayor a mayor</option>
          </select>
        </div>
      </div>

      {/* species chips */}
      <div className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-moss mb-2">Especie</p>
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {SPECIES_CHIPS.map((s) => (
            <button key={s.slug} data-testid={`species-chip-${s.slug}`} onClick={() => setSpecies(s.slug)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-all ${species === s.slug ? "bg-charcoal text-cream border-charcoal" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>{s.name}</button>
          ))}
        </div>
      </div>

      {/* category chips */}
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-moss mb-2">Categoría <span className="normal-case font-medium text-muted-moss/70">· toca varias para combinar</span></p>
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {categoryChips.map((c) => {
            const active = c.slug === "todos" ? selectedCats.length === 0 : selectedCats.includes(c.slug);
            return (
              <button key={c.slug} data-testid={`category-chip-${c.slug}`} onClick={() => toggleCategory(c.slug)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-all ${active ? "bg-coral text-white border-coral card-shadow" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>{c.name}</button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 mb-16">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-3xl border border-[#E3E7E2] overflow-hidden animate-pulse"><div className="aspect-square bg-cream-2" /><div className="p-4 space-y-2"><div className="h-4 bg-cream-2 rounded" /><div className="h-4 w-1/2 bg-cream-2 rounded" /></div></div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-moss">
            <PawPrint className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-semibold">No encontramos productos</p>
            <p className="text-sm">Prueba con otra especie, categoría o búsqueda.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-moss mb-4">{filtered.length} producto(s)</p>
            <div data-testid="product-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
